import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Phone, Mail, Check, RefreshCw, UserX, Footprints, Send } from "lucide-react";
import { api } from "../console/api";
import { Groupe, TitreGroupe, Retour, Vide, Badge, Segments } from "../espace/ui";
import { CalendrierAnnee } from "../components/CalendrierAnnee";
import { Profs } from "../espace/Cartes";
import { iso, decaler, jourLisible, tel } from "./outils";

const simple = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
const enseigne = (prenom, profs) => !!prenom && simple(profs).split(/[^a-z]+/).includes(prenom);

const Personne = ({ p, type, appel, onCocher }) => {
  const [ouvert, setOuvert] = useState(false);
  const present = appel[p.eleve_id] === "present";
  return (
    <div data-testid="seance-personne">
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => onCocher(p.eleve_id)} aria-label="Présent"
          className="w-8 h-8 rounded-full grid place-items-center shrink-0 transition-colors"
          style={present ? { background: "#16A34A", color: "#fff" } : { border: "2px solid #CBD5E1" }}
          disabled={p.absent}>
          {present && <Check size={16} strokeWidth={3} />}
        </button>
        <button onClick={() => setOuvert(!ouvert)} className="flex-1 min-w-0 text-left">
          <span className="block text-[15px] leading-snug"
            style={p.absent ? { color: "#94A3B8", textDecoration: "line-through" } : undefined}>
            {p.eleve}{p.age ? <span className="text-[var(--muted)]"> · {p.age} ans</span> : null}
          </span>
          <span className="flex flex-wrap gap-1.5 mt-1">
            {type === "essai" && <Badge couleur="#00A8AF">Essai</Badge>}
            {p.absent && <Badge couleur="#F59E0B">Absent·e</Badge>}
            {p.nouveau && <Badge couleur="#2563EB">Nouveau</Badge>}
            {p.fusion && <Badge couleur="#64748B">Fusion{p.fusion.avant ? ` · avant ${p.fusion.avant}` : ""}</Badge>}
            {p.carte10 && (p.carte10_etat
              // Carte 10 numérique : séances utilisées, ou « terminée » quand il en faut une nouvelle.
              ? <Badge couleur={p.carte10_etat.statut === "en_cours" ? "#0D9488" : "#DC2626"}>
                {p.carte10_etat.statut === "en_cours" ? `Carte ${p.carte10_etat.n}/${p.carte10_etat.nb}` : "Carte 10 terminée"}
                {p.carte10_etat.hors_carte ? ` · ${p.carte10_etat.hors_carte} hors carte` : ""}</Badge>
              : <Badge couleur="#B45309">Carte 10 cours</Badge>)}
            {p.anniversaire && <Badge couleur="#DB2777">🎂 Anniversaire</Badge>}
            {p.desinscrit && <Badge couleur="#94A3B8">Désinscrit·e{p.dernier_cours_le ? ` · dernier cours ${p.dernier_cours_le.split("-").reverse().join(".")}` : ""}</Badge>}
            {String(p.droit_image || "").toLowerCase().startsWith("non") && <Badge couleur="#DC2626">Pas de photo</Badge>}
          </span>
          {p.absent && p.absence_motif && (
            <span className="block text-[12.5px] leading-snug mt-1 break-words" style={{ color: "#B45309" }}>{p.absence_motif}</span>
          )}
        </button>
      </div>
      {ouvert && (
        <div className="px-4 pb-3 -mt-1 space-y-2 text-[13px]">
          <div className="text-[var(--muted)]">{p.responsable}{p.infos ? ` · ${p.infos}` : ""}</div>
          <div className="flex gap-2">
            {p.telephone && <a href={tel(p.telephone)} className="btn-ghost text-xs"><Phone size={13} /> Appeler</a>}
            {p.email && <a href={`mailto:${p.email}`} className="btn-ghost text-xs"><Mail size={13} /> Écrire</a>}
          </div>
        </div>
      )}
    </div>
  );
};

/* « Cours annulé ce soir », « Pensez aux chaussures » : un e-mail à toutes les familles du
   cours, déposé aussi dans leur application. */
const EcrireAuCours = ({ coursId, nom }) => {
  const [ouvert, setOuvert] = useState(false);
  const [sujet, setSujet] = useState(`${nom} — information`);
  const [texte, setTexte] = useState("");
  const [etat, setEtat] = useState("");
  const envoyer = async () => {
    setEtat("…");
    try {
      const { items } = await api(`/envois/destinataires?type=cours&id=${encodeURIComponent(coursId)}`);
      const emails = items.map((x) => x.email).filter(Boolean);
      if (!emails.length) { setEtat("Aucune famille à prévenir dans ce cours."); return; }
      if (!window.confirm(`Envoyer ce message à ${emails.length} famille${emails.length > 1 ? "s" : ""} ?`)) { setEtat(""); return; }
      const corps = new FormData();
      corps.append("corps", JSON.stringify({ type: "cours", id: coursId, groupe: nom, sujet, message: texte, emails, espace: true }));
      await api("/envois", { method: "POST", body: corps });
      setEtat(`Envoyé à ${emails.length} famille${emails.length > 1 ? "s" : ""}.`); setTexte(""); setOuvert(false);
    } catch (e) { setEtat(e.message); }
  };
  return (
    <Groupe>
      <button onClick={() => setOuvert(!ouvert)} className="w-full flex items-center gap-3 px-4 py-3.5 text-left active:bg-[#F1F5F9]"
        data-testid="seance-ecrire">
        <span className="w-[30px] h-[30px] rounded-[9px] grid place-items-center text-white" style={{ background: "#00A8AF" }}><Send size={16} /></span>
        <span className="flex-1">
          <span className="block text-[15px]">Écrire aux familles du cours</span>
          <span className="block text-[12.5px] text-[var(--muted)]">E-mail + message dans leur application</span>
        </span>
        <ChevronRight size={18} style={{ color: "#C4C9D0", transform: ouvert ? "rotate(90deg)" : undefined }} />
      </button>
      {ouvert && (
        <div className="p-4 space-y-2">
          <input className="input-field w-full" value={sujet} onChange={(e) => setSujet(e.target.value)} />
          <textarea className="input-field w-full" rows={4} value={texte} onChange={(e) => setTexte(e.target.value)}
            placeholder="Ex. Le cours de ce soir est annulé, il sera rattrapé le…" />
          <button className="btn-primary w-full justify-center" disabled={!texte.trim() || !sujet.trim()} onClick={envoyer}
            data-testid="seance-ecrire-envoyer"><Send size={14} /> Envoyer</button>
        </div>
      )}
      {etat && <p className="px-4 py-3 text-[13px] text-[var(--muted)]">{etat}</p>}
    </Groupe>
  );
};

/* Une séance : la feuille de présence du jour, au pouce. `le` vide = la prochaine séance. */
const Seance = ({ coursId, le: demande, onRetour }) => {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const le = demande || d?.seance_le || "";
  // L'appel est le même que celui du prof : enregistré à l'école, visible des deux côtés.
  const [appel, setAppel] = useState({});
  const [avis, setAvis] = useState("");
  useEffect(() => {
    api(`/mobile/cours/${encodeURIComponent(coursId)}${demande ? `?le=${demande}` : ""}`)
      .then((x) => { setD(x); setAppel(x.appel || {}); }).catch((e) => setErreur(e.message));
  }, [coursId, demande]);
  const cocher = (id) => {
    const suite = { ...appel };
    if (suite[id] === "present") delete suite[id]; else suite[id] = "present";
    setAppel(suite);
    // Seul l'élève touché part à l'école ("" = coche retirée) : le prof peut faire
    // l'appel en même temps sans que l'un efface l'autre.
    api(`/mobile/appel/${encodeURIComponent(coursId)}/${le}`, { method: "PUT", body: JSON.stringify({ etats: { [id]: suite[id] || "" } }) })
      .then(() => {
        setAvis("");
        // Carte 10 : « Carte 7/10 » suit la coche tout de suite (avant : seulement en rouvrant la séance).
        if (!d.inscrits.some((p) => p.carte10_etat && p.eleve_id === id)) return;
        api(`/mobile/cours/${encodeURIComponent(coursId)}?le=${le}`).then((x) => {
          const cartes = Object.fromEntries((x.inscrits || []).map((p) => [p.eleve_id, p.carte10_etat]));
          setD((v) => ({ ...v, inscrits: v.inscrits.map((p) => (p.carte10_etat ? { ...p, carte10_etat: cartes[p.eleve_id] || p.carte10_etat } : p)) }));
        }).catch(() => { /* le badge se met à jour à la prochaine ouverture */ });
      }).catch((e) => setAvis(e.message));
  };
  if (erreur) return <><Retour onClick={onRetour} /><Vide>{erreur}</Vide></>;
  if (!d) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;
  const essais = (d.essais || []).filter((e) => String(e.date || "").slice(0, 10) === le);
  const presents = Object.values(appel).filter((v) => v === "present").length;
  return (
    <div className="space-y-5" data-testid="ecole-seance">
      <Retour onClick={onRetour} label="Journée" />
      <div>
        <div className="font-display text-[24px] leading-tight">{d.entete.titre}</div>
        <div className="text-[13px] text-[var(--muted)] mt-1">{jourLisible(le)} · {d.cours.debut}–{d.cours.fin}{d.cours.salle ? ` · ${/^salle/i.test(d.cours.salle) ? d.cours.salle : `salle ${d.cours.salle}`}` : ""}</div>
        {d.cours.professeur && <Profs texte={d.cours.professeur} />}
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        {[["Inscrits", d.inscrits.length], ["Essais", essais.length], ["Présents", presents]].map(([l, n]) => (
          <div key={l} className="rounded-2xl bg-white py-3" style={{ border: "1px solid var(--border)" }}>
            <div className="text-[22px] font-semibold tabular-nums">{n}</div>
            <div className="text-[11.5px] text-[var(--muted)]">{l}</div>
          </div>
        ))}
      </div>
      <section>
        <TitreGroupe>Inscrits</TitreGroupe>
        <Groupe>
          {d.inscrits.length ? d.inscrits.map((p) => (
            <Personne key={p.inscription_id || p.eleve_id} p={p} type="inscrit" appel={appel} onCocher={cocher} />
          )) : <p className="px-4 py-4 text-sm text-[var(--muted)]">Personne d'inscrit.</p>}
        </Groupe>
      </section>
      {essais.length > 0 && (
        <section>
          <TitreGroupe>Essais du jour</TitreGroupe>
          <Groupe>
            {essais.map((p) => <Personne key={p.eleve_id} p={p} type="essai" appel={appel} onCocher={cocher} />)}
          </Groupe>
        </section>
      )}
      {avis && <p className="text-sm" style={{ color: "#B91C1C" }}>{avis}</p>}
      <EcrireAuCours coursId={coursId} nom={d.entete.titre} />
      <p className="text-[12px] text-[var(--muted)] px-1">
        L'appel est enregistré à l'école{d.appel_par ? ` (dernière fois par ${d.appel_par})` : ""} : le prof et la direction voient le même.
      </p>
    </div>
  );
};

/* La journée : tous les cours du jour, ce qui est attendu, et ce qui cloche (prof absent,
   élèves absents, essais). */
/* Un stage ou un workshop ce jour-là : en tête de la journée, il mène à son appel. */
const EvenementsDuJour = ({ le, aller }) => {
  const [ev, setEv] = useState(null);
  useEffect(() => { api("/mobile/evenements").then(setEv).catch(() => {}); }, []);
  if (!ev || !aller) return null;
  // Un jour du stage (pas le week-end au milieu), et seulement les enfants attendus ce jour-là.
  const stages = ev.stages.filter((s) => (s.jours?.length ? s.jours.includes(le) : s.debut <= le && le <= s.fin))
    .map((s) => ({ ...s, attendus: s.participants.filter((p) => !p.jours?.length || p.jours.includes(le)) }));
  const ws = ev.workshops.seances.filter((x) => x.date === le);
  if (!stages.length && !ws.length) return null;
  return (
    <Groupe>
      {stages.map((s) => (
        <button key={s.id} onClick={() => aller("plus", { ecran: "evenements", id: s.id, jour: le })}
          className="w-full flex items-center gap-3 px-4 py-3.5 text-left" data-testid={`journee-stage-${s.id}`}>
          <span className="w-[30px] h-[30px] rounded-[9px] grid place-items-center text-white shrink-0" style={{ background: "#F59E0B" }}>★</span>
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-semibold">{s.nom}</span>
            <span className="block text-[12.5px] text-[var(--muted)]">Stage · {s.attendus.length} enfant{s.attendus.length > 1 ? "s" : ""} attendu{s.attendus.length > 1 ? "s" : ""} · faire l'appel</span>
          </span>
          <ChevronRight size={18} style={{ color: "#C4C9D0" }} />
        </button>
      ))}
      {ws.map((x) => (
        <button key={x.id} onClick={() => aller("plus", { ecran: "evenements", id: x.id })}
          className="w-full flex items-center gap-3 px-4 py-3.5 text-left">
          <span className="w-[30px] h-[30px] rounded-[9px] grid place-items-center text-white shrink-0" style={{ background: "#EC4899" }}>♥</span>
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-semibold">Workshop heels · {x.debut}–{x.fin}</span>
            <span className="block text-[12.5px] text-[var(--muted)]">{x.participants.length} inscrite{x.participants.length > 1 ? "s" : ""} · paiements et appel</span>
          </span>
          <ChevronRight size={18} style={{ color: "#C4C9D0" }} />
        </button>
      ))}
    </Groupe>
  );
};

export const Journee = ({ cible, aller }) => {
  const [le, setLe] = useState(() => iso(new Date()));
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  const [cours, setCours] = useState(null);
  const [seanceLe, setSeanceLe] = useState("");
  const [vue, setVue] = useState("jour");
  // Les directeurs enseignent aussi : leurs propres cours sont signalés « Ton cours ».
  const [moi, setMoi] = useState("");
  useEffect(() => { api("/moi").then((u) => setMoi(simple(u.nom).split(" ")[0])).catch(() => {}); }, []);
  // Arrivée depuis une alerte (« cours surréservé »…) : on ouvre directement la séance.
  useEffect(() => {
    if (cible?.cours) { setSeanceLe(cible.le || ""); setCours(cible.cours); }
    if (cible?.racine) setCours(null);
  }, [cible]);

  const charger = useCallback(() => {
    setD(null); setErreur("");
    api(`/mobile/jour?le=${le}`).then(setD).catch((e) => setErreur(e.message));
  }, [le]);
  useEffect(charger, [charger]);

  if (cours) return <Seance coursId={cours} le={seanceLe} onRetour={() => setCours(null)} />;

  const aujourdhui = le === iso(new Date());
  const choix = <Segments testId="ecole-vue" valeur={vue} onChange={setVue}
    options={[{ id: "jour", label: "Jour" }, { id: "annee", label: "L'année" }]} />;
  if (vue === "annee") return <div className="space-y-5">{choix}<CalendrierAnnee testId="ecole-annee" /></div>;
  return (
    <div className="space-y-5" data-testid="ecole-journee">
      {choix}
      <div className="flex items-center justify-between rounded-[22px] bg-white px-2 py-1.5"
        style={{ border: "1px solid var(--border)" }}>
        <button onClick={() => setLe(decaler(le, -1))} aria-label="Jour précédent"
          className="w-11 h-11 grid place-items-center rounded-full active:bg-[#F1F5F9]"><ChevronLeft size={20} /></button>
        <button onClick={() => setLe(iso(new Date()))} className="text-center">
          <div className="text-[15px] font-semibold capitalize">{jourLisible(le)}</div>
          <div className="text-[11.5px]" style={{ color: aujourdhui ? "var(--primary)" : "var(--muted)" }}>
            {aujourdhui ? "Aujourd'hui" : "Revenir à aujourd'hui"}
          </div>
        </button>
        <button onClick={() => setLe(decaler(le, 1))} aria-label="Jour suivant"
          className="w-11 h-11 grid place-items-center rounded-full active:bg-[#F1F5F9]"><ChevronRight size={20} /></button>
      </div>

      <EvenementsDuJour le={le} aller={aller} />
      {erreur && <Vide>{erreur}</Vide>}
      {!d && !erreur && <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>}
      {d?.ferme && (
        <div className="rounded-[22px] px-4 py-3 text-sm" style={{ background: "#FEF3C7", color: "#92400E" }}>
          École fermée ce jour-là (vacances ou jour férié).
        </div>
      )}
      {d && !d.cours.length && <Vide>Aucun cours ce jour-là.</Vide>}
      {d && d.cours.length > 0 && (
        <section>
          <TitreGroupe>{d.cours.length} cours</TitreGroupe>
          <Groupe testId="ecole-journee-cours">
            {d.cours.map((c) => (
              <button key={c.id} onClick={() => { setSeanceLe(le); setCours(c.id); }} data-testid={`ecole-cours-${c.id}`}
                className="w-full flex gap-3.5 px-4 py-3.5 text-left active:bg-[#F1F5F9]">
                <div className="w-[54px] shrink-0 self-start rounded-2xl text-center py-2" style={{ background: "var(--primary-tint)" }}>
                  <div className="text-[15px] font-semibold tabular-nums leading-tight">{c.debut}</div>
                  <div className="text-[11px] text-[var(--muted)] tabular-nums">{c.fin}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold leading-snug">{c.nom}</div>
                  <div className="text-[12.5px] text-[var(--muted)] mt-0.5">
                    {[c.professeur, c.salle ? `salle ${c.salle}` : ""].filter(Boolean).join(" · ")}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {enseigne(moi, c.professeur) && <Badge couleur="#9333EA">Ton cours</Badge>}
                    <Badge couleur="#0F1B2D">{c.attendus} attendu{c.attendus > 1 ? "s" : ""}{c.places_max ? ` / ${c.places_max}` : ""}</Badge>
                    {c.essais > 0 && <Badge couleur="#00A8AF">{c.essais} essai{c.essais > 1 ? "s" : ""}</Badge>}
                    {c.absents > 0 && <Badge couleur="#F59E0B">{c.absents} absent{c.absents > 1 ? "s" : ""}</Badge>}
                  </div>
                  {c.profs_absents.map((a) => (
                    <div key={a.id} className="text-[12.5px] mt-2 flex items-center gap-1.5" style={{ color: "#B45309" }}>
                      <UserX size={13} /> {a.prof} absent·e — {a.remplacant ? `remplacé·e par ${a.remplacant}` : "remplaçant à trouver"}
                    </div>
                  ))}
                </div>
                <ChevronRight size={18} className="self-center shrink-0" style={{ color: "#C4C9D0" }} />
              </button>
            ))}
          </Groupe>
        </section>
      )}
      {d && d.essais.length > 0 && (
        <section>
          <TitreGroupe>Essais du jour</TitreGroupe>
          <Groupe>
            {d.essais.map((e) => (
              <div key={`${e.eleve_id}-${e.cours_id}`} className="flex items-center gap-3 px-4 py-3">
                <Footprints size={18} style={{ color: "#00A8AF" }} />
                <div className="min-w-0">
                  <div className="text-[15px]">{e.eleve}</div>
                  <div className="text-[12.5px] text-[var(--muted)]">{e.cours}</div>
                </div>
              </div>
            ))}
          </Groupe>
        </section>
      )}
    </div>
  );
};
